/**
 * studio-shop D4 — the bag (`shop-bag`): one piece, two drawings. The page at
 * `/shop/bag` places it on every site; a custom page that places it gets it as
 * a drawer, which its bag button and Add to bag open. The bag is kept in this browser for the studio's
 * address and priced by the server from the catalog each time it shows: a price
 * that changed since the item went in is marked, and an item no longer sold is
 * marked and left out of the subtotal and the order. shop-self-fulfilment D5:
 * so is an item with fewer left than its quantity, saying Sold out or how many
 * are left.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'shop-bag',
  description:
    'The bag: each item with its picture, name, option, quantity and price, the subtotal, and Checkout. Priced from the catalog each time it shows; a changed price and an item no longer sold are marked.',
  placeable: true,
  options: {},
  parts: {
    bag: { description: 'The whole bag.', required: false },
    title: { description: 'Your bag.', required: false },
    close: { description: "Closes the bag's drawer.", required: false },
    line: { description: 'One item in the bag.', required: true },
    image: { description: "The item's picture.", required: false, role: 'photo' },
    name: { description: "The product's name.", required: true, role: 'title' },
    option: { description: 'The option chosen: format and size.', required: true },
    quantity: { description: 'How many, with one fewer and one more.', required: true },
    remove: { description: 'Takes the item out of the bag.', required: true },
    price: { description: "The item's price for its quantity.", required: true },
    changed: { description: 'The price changed since the item went in the bag, with the price it was.', required: true },
    unavailable: { description: 'The item is no longer available, or sold out (with how many are left), and stays out of the order.', required: true },
    subtotal: { description: 'The subtotal of the items still available.', required: true },
    note: { description: 'Where shipping is free; other countries see theirs at checkout.', required: false },
    checkout: { description: 'Checkout, with the items still available.', required: true, states: ['disabled'], role: 'primary-action' },
    keepShopping: { description: 'Back to the studio.', required: false },
    empty: { description: 'The bag is empty.', required: false },
    problem: { description: "Why the bag can't be priced or bought now, in the server's words.", required: true },
  },
  actions: {
    setQuantity: {
      description: 'Sets how many of one item (1 to 10); 0 takes it out.',
      args: [
        { name: 'optionId', kind: 'item', list: 'lines', key: 'optionId' },
        { name: 'quantity', kind: 'value', type: 'number', min: 0, max: 10 },
      ],
    },
    remove: { description: 'Takes one item out of the bag.', args: [{ name: 'optionId', kind: 'item', list: 'lines', key: 'optionId' }] },
    checkout: { description: 'Goes to checkout with the items still available.', args: [] },
    keepShopping: { description: 'Leaves the bag for the studio: closes the drawer, or from the bag page goes home.', args: [] },
    open: { description: "Opens the bag's drawer.", args: [] },
    close: { description: "Closes the bag's drawer.", args: [] },
  },
  events: {},
});

/** One item in the bag, as the server priced it. */
export interface ShopBagLine {
  /** The option's id: what the actions name. */
  readonly optionId: string;
  /** The product's name; null when the item is no longer in the catalog. */
  readonly name: string | null;
  /** The option's label ("Paper 18 × 24 in"); null when the item is no longer in the catalog. */
  readonly option: string | null;
  /** The option's picture, else the product's first; or null. */
  readonly imageUrl: string | null;
  /** The size whose shape the picture is drawn in ("18 × 24 in"); null for the product's own picture, drawn whole. */
  readonly size: string | null;
  /** The picture is a canvas's own: draw only the canvas front (the kit's PrintPicture does), not the wrap around the stretcher. */
  readonly canvas: boolean;
  /** How many, 1 to 10. */
  readonly quantity: number;
  /** The price of one: "$95.00"; null when no longer available. */
  readonly unitPrice: string | null;
  /** The price for the quantity: "$190.00"; null when no longer available. */
  readonly total: string | null;
  /** The price of one when it went in the bag ("$90.00"), only while it differs from today's; null otherwise. */
  readonly was: string | null;
  /** False when the item is no longer sold: it stays out of the subtotal and the order. */
  readonly available: boolean;
  /** Fewer are left than its quantity, so it is not available (shop-self-fulfilment D5). */
  readonly soldOut: boolean;
  /** When sold out, how many are left (0 for none); null otherwise. */
  readonly left: number | null;
  /** site-form-buy-mode: a website form put it in with its answers, so it stays at one. */
  readonly claimed: boolean;
}

/** What the bag knows. */
export interface ShopBagState {
  /** True while the bag shows: always on the bag page; while it is open, in a drawer. */
  readonly open: boolean;
  /** loading while it is priced, empty, ready, or failed (`problem` says why). */
  readonly status: 'loading' | 'empty' | 'ready' | 'failed';
  readonly lines: readonly ShopBagLine[];
  /** The subtotal of the available items: "$285.00". */
  readonly subtotal: string | null;
  /** How many items are in the bag, every quantity added up. */
  readonly count: number;
  /** True when at least one item is available to buy. */
  readonly canCheckout: boolean;
  /** Why the bag can't be priced or bought now, in the server's words; null otherwise. */
  readonly problem: string | null;
  /** The most of one item a bag holds. */
  readonly maxQuantity: number;
  /** The shipping line: "Free shipping in the United States. Other countries see their shipping at checkout.", or "Shipping and tax are worked out at checkout." */
  readonly shipping: string;
}

/** What a drawing can ask the bag to do. */
export interface ShopBagActions {
  /** Sets how many of the option, 1 to 10; 0 takes it out. */
  readonly setQuantity: (optionId: string, quantity: number) => void;
  /** Takes the option out of the bag. */
  readonly remove: (optionId: string) => void;
  /** Goes to checkout. */
  readonly checkout: () => void;
  /** Leaves the bag for the studio. */
  readonly keepShopping: () => void;
  /** Opens the drawer. */
  readonly open: () => void;
  /** Closes the drawer. */
  readonly close: () => void;
}

/** The bag reports nothing to its host. */
export type ShopBagEvents = Record<string, never>;
